import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import { MusicListItemDto, SongTags } from '~/models/music/music.dto';
import css from '~/features/dashboard/components/songs-list/styles.module.scss';
import { DateFormat } from '~/common/date-format';
import { ActionsCell } from '~/features/dashboard/components/songs-list/components/ActionsCell';
import { KeySongPage } from '~/models/music/new-song/helpers';
import { Tooltip } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';

interface propsListView {
    artist: string;
    title: string | null;
    writingSharePercent: string;
    releaseDate: string | null;
    gSongReleaseYear: string | null;
    label: string | null;
    entity: MusicListItemDto;
}

interface Interface {
    data: propsListView[];
    pageTab: number;
}

function descendingComparator(a: any, b: any, orderBy: any) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order: any, orderBy: any) {
    return order === 'desc' ? (a: any, b: any) => descendingComparator(a, b, orderBy) : (a: any, b: any) => -descendingComparator(a, b, orderBy);
}

function stableSort(array: any, comparator: any) {
    const stabilizedThis = array.map((el: any, index: any) => [el, index]);
    stabilizedThis.sort((a: any, b: any) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el: any) => el[0]);
}

function EnhancedTableHead(props: any) {
    const { classes, order, orderBy, onRequestSort, page } = props;
    const createSortHandler = (property: any) => (event: any) => {
        onRequestSort(event, property);
    };

    const headCells = [
        [
            { id: 'title', numeric: false, disablePadding: false, label: 'Song name' },
            { id: 'artist', numeric: false, disablePadding: false, label: 'Artist' },
            { id: 'writingSharePercent', numeric: true, disablePadding: false, label: 'Writing Share' },
            { id: 'releaseDate', numeric: true, disablePadding: false, label: 'Release Date' },
            { id: 'label', numeric: true, disablePadding: false, label: 'Label' },
            { id: 'entity', numeric: false, disablePadding: false, label: 'Action' },
        ],
        [
            { id: 'title', numeric: false, disablePadding: false, label: 'Song name' },
            { id: 'writingSharePercent', numeric: true, disablePadding: false, label: 'Writing Share' },
            { id: 'behaviour', numeric: false, disablePadding: false, label: 'Behaviour' },
            { id: 'classification', numeric: false, disablePadding: false, label: 'Classification' },
            { id: 'context', numeric: false, disablePadding: false, label: 'Context' },
            { id: 'era', numeric: false, disablePadding: false, label: 'Era' },
            { id: 'genre', numeric: false, disablePadding: false, label: 'Genre' },
            { id: 'mood', numeric: false, disablePadding: false, label: 'Mood' },
            { id: 'description', numeric: false, disablePadding: false, label: 'Description' },
            { id: 'entity', numeric: false, disablePadding: false, label: 'Action' },
        ],
        [
            { id: 'title', numeric: false, disablePadding: false, label: 'Song name' },
            { id: 'artist', numeric: false, disablePadding: false, label: 'Artist' },
            { id: 'writingSharePercent', numeric: true, disablePadding: false, label: 'Writing Share' },
            { id: 'releaseDate', numeric: true, disablePadding: false, label: 'Release Date' },
            { id: 'label', numeric: true, disablePadding: false, label: 'Label' },
            { id: 'entity', numeric: false, disablePadding: false, label: 'Action' },
        ],
        [
            { id: 'title', numeric: false, disablePadding: false, label: 'Song name' },
            { id: 'artist', numeric: false, disablePadding: false, label: 'Artist' },
            { id: 'writingSharePercent', numeric: true, disablePadding: false, label: 'Writing Share' },
            { id: 'releaseDate', numeric: true, disablePadding: false, label: 'Release Date' },
            { id: 'apple', numeric: true, disablePadding: false, label: 'Apple Music' },
            { id: 'spotify', numeric: true, disablePadding: false, label: 'Spotify' },
            { id: 'youtube', numeric: true, disablePadding: false, label: 'YouTube' },
            { id: 'entity', numeric: false, disablePadding: false, label: 'Action' },
        ],
    ];

    return (
        <TableHead>
            <TableRow>
                {headCells[page].map((headCell, index) => (
                    <TableCell
                        key={headCell.id}
                        sortDirection={orderBy === headCell.id ? order : false}
                        style={headCells.length - 1 !== index ? { width: '20%' } : { width: '10%' }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
};

const useStyles = makeStyles(theme => ({
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    table: {
        minWidth: 750,
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
}));

export const GridTable: React.FC<Interface> = ({ data, pageTab }) => {
    const classes = useStyles();
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('title');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    const handleRequestSort = (event: any, property: any) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const findIndex = (tags: SongTags[], str: string) => {
        return tags.findIndex(({ type }) => type.toLowerCase() === str.toLowerCase());
    };

    const printTags = (row: any, title: string) => {
        let tags: string[] = row.tags[findIndex(row.tags, title)]?.value || [];
        return tags.map(item => <Chip style={{ backgroundColor: 'white', marginBottom: '5px', marginRight: '5px' }} label={item} />);
    };

    const renderSwitch = (param: number, row: any) => {
        let desc: string, descCrop: string;
        switch (param) {
            case KeySongPage.Synch:
                desc = row.tags[findIndex(row.tags, 'description')]?.value.join(' ') || '';
                descCrop = desc.length > 40 ? desc.substr(0, 40) + ' ...' : desc;
                return (
                    <>
                        <TableCell style={{ width: '15%' }}>{row.title}</TableCell>
                        <TableCell style={{ width: '15%' }}>{row.writingSharePercent}</TableCell>
                        <TableCell style={{ width: '10%' }}>{printTags(row, 'behaviour')}</TableCell>
                        <TableCell style={{ width: '10%' }}>{printTags(row, 'classification')}</TableCell>
                        <TableCell style={{ width: '10%' }}>{printTags(row, 'context')}</TableCell>
                        <TableCell style={{ width: '10%' }}>{printTags(row, 'era')}</TableCell>
                        <TableCell style={{ width: '10%' }}>{printTags(row, 'genre')}</TableCell>
                        <TableCell style={{ width: '10%' }}>{printTags(row, 'mood')}</TableCell>
                        <TableCell style={{ width: '10%' }}>
                            <Tooltip title={desc} aria-label="add">
                                <span>{descCrop}</span>
                            </Tooltip>
                        </TableCell>
                        <TableCell style={{ width: '5%' }}>
                            <ActionsCell data={row.entity} />
                        </TableCell>
                    </>
                );
            case KeySongPage.DSPs:
            case KeySongPage.Stateuses:
            default:
                return (
                    <>
                        <TableCell style={{ width: '20%' }}>{row.title}</TableCell>
                        <TableCell style={{ width: '20%' }}>{row.artist}</TableCell>
                        <TableCell style={{ width: '20%' }}>{row.writingSharePercent}</TableCell>
                        <TableCell style={{ width: '20%' }}>
                            {(row.releaseDate === '' || row.releaseDate === '1900-01-01T00:00:00') && !row.gSongReleaseYear ? (
                                'MISSING'
                            ) : row.gSongReleaseYear !== '' && (row.releaseDate === '1900-01-01T00:00:00' || row.releaseDate === '') ? (
                                row.gSongReleaseYear
                            ) : (
                                <DateFormat newDate={row.releaseDate} />
                            )}
                        </TableCell>
                        <TableCell style={{ width: '20%' }}>{row.label}</TableCell>
                        <TableCell style={{ width: '10%' }}>
                            <ActionsCell data={row.entity} />
                        </TableCell>
                    </>
                );
        }
    };
    return (
        <div>
            <TableContainer component={Paper} className={css.tableKeyDates}>
                <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
                    <EnhancedTableHead
                        classes={classes}
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={data.length}
                        page={pageTab}
                    />
                    <TableBody style={{ backgroundColor: '#daebec' }}>
                        {stableSort(data, getComparator(order, orderBy))
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row: any, index: any) => {
                                const labelId = `enhanced-table-${index}`;

                                return (
                                    <TableRow hover key={labelId}>
                                        {renderSwitch(pageTab, row)}
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
            />
        </div>
    );
};
